"use client";

import {
  ArrowRight,
  BookOpen,
  Building2,
  CheckCircle2,
  Clock,
  ExternalLink,
  Laptop,
  Lightbulb,
  LogIn,
  Phone,
  PhoneCall,
  Plus,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Store,
  Tag,
  Zap,
} from "lucide-react";
import Link from "next/link";
import { useSession } from "next-auth/react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { CustomerListingForm } from "@/components/marketplace/customer-listing-form";
import { MyListingsView } from "@/components/marketplace/my-listings-view";
import { Button } from "@/components/ui/button";
import { apiGet, apiPost } from "@/services/api";

type VendorType = { id: string; name: string; slug: string };

type SellTab = "post" | "my-listings" | "vendor";

export function SellPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: session, status } = useSession();

  const tabParam = searchParams.get("tab") as SellTab | null;
  const [activeTab, setActiveTab] = useState<SellTab>(tabParam || "post");
  const [livePhone, setLivePhone] = useState("+251 91 123 4567");

  const [types, setTypes] = useState<VendorType[]>([]);
  const [existingVendor, setExistingVendor] = useState<{ status: string } | null>(null);
  const [vendorForm, setVendorForm] = useState({
    businessName: "",
    vendorTypeId: "",
    ownerName: "",
    phone: "",
    address: "",
    description: "",
    businessLicense: "",
    taxId: "",
  });
  const [submittingVendor, setSubmittingVendor] = useState(false);

  useEffect(() => {
    if (tabParam && (tabParam === "post" || tabParam === "my-listings" || tabParam === "vendor")) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const handleTabChange = (tab: SellTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  useEffect(() => {
    if (session) {
      void apiGet<{ vendorTypes: VendorType[] }>("/api/store/vendor-types")
        .then((d) => setTypes(d?.vendorTypes ?? []))
        .catch(() => setTypes([]));
      void apiGet<{ vendor: { status: string } }>("/api/vendors/me")
        .then((d) => setExistingVendor(d?.vendor ?? null))
        .catch(() => setExistingVendor(null));
    }
  }, [session]);

  async function submitVendorApplication() {
    setSubmittingVendor(true);
    try {
      await apiPost("/api/vendors/apply", vendorForm);
      toast.success("Application submitted — awaiting admin review");
      navigate("/");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not apply");
    } finally {
      setSubmittingVendor(false);
    }
  }

  return (
    <div className="min-h-screen w-full bg-[#f8fafc] text-slate-800 dark:bg-slate-950 dark:text-slate-100 flex flex-col">
      {/* =========================================================================
           HERO / VALUE STATEMENT BANNER (EXACT REFERENCE DESIGN)
           ========================================================================= */}
      <section className="relative overflow-hidden border-b border-white/10 bg-gradient-to-br from-slate-950 via-[#073B70] to-[#0d1b3e] text-white py-12 lg:py-16">
        {/* Ambient radial blur */}
        <div className="pointer-events-none absolute -right-20 -top-20 h-[500px] w-[500px] rounded-full bg-radial from-[#087DB5]/30 to-transparent blur-3xl" />
        <div className="pointer-events-none absolute -left-20 -bottom-20 h-[400px] w-[400px] rounded-full bg-radial from-[#00B7B0]/20 to-transparent blur-2xl" />

        <div className="relative z-10 mx-auto w-full max-w-[1280px] px-4 sm:px-6">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            {/* Left: Value Prop */}
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/15 px-3.5 py-1 text-xs font-semibold text-emerald-400 mb-4 shadow-2xs">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                </span>
                100% Free Community Marketplace • Ethiopia
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight">
                Sell Phones &amp; Laptops <br />
                <span className="bg-gradient-to-r from-[#60a5fa] via-[#38bdf8] to-[#818cf8] bg-clip-text text-transparent">
                  Directly to Buyers
                </span>
              </h1>

              <p className="mt-3.5 text-sm sm:text-base text-slate-300 leading-relaxed font-normal max-w-xl">
                Zero transaction cuts, zero upfront listing fees. Post your smartphone, MacBook, or Windows desktop in 2 minutes and take calls directly from active buyers across Addis Ababa and regional hubs.
              </p>

              {/* Action Buttons */}
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleTabChange("post")}
                  className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold shadow-lg transition-all ${
                    activeTab === "post"
                      ? "bg-[#087DB5] text-white hover:bg-[#073B70] shadow-[#087DB5]/30"
                      : "bg-[#087DB5]/90 text-white hover:bg-[#087DB5]"
                  }`}
                >
                  <Plus className="h-4 w-4" />
                  Post Free Listing
                </button>

                <button
                  type="button"
                  onClick={() => handleTabChange("my-listings")}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium border transition-all ${
                    activeTab === "my-listings"
                      ? "border-white bg-white/20 text-white"
                      : "border-white/15 bg-white/5 text-white hover:bg-white/10"
                  }`}
                >
                  <Tag className="h-4 w-4 text-sky-400" />
                  My Listings &amp; Status
                </button>

                <button
                  type="button"
                  onClick={() => handleTabChange("vendor")}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium border transition-all ${
                    activeTab === "vendor"
                      ? "border-white bg-white/20 text-white"
                      : "border-white/15 bg-white/5 text-white hover:bg-white/10"
                  }`}
                >
                  <Store className="h-4 w-4 text-amber-300" />
                  Commercial Store
                </button>
              </div>
            </div>

            {/* Right: Value Proposition Badges (4 Pillar Cards) */}
            <div className="grid grid-cols-2 gap-3 w-full lg:max-w-[440px] shrink-0">
              {/* Pillar 1: 100% Free */}
              <div className="flex items-center gap-3.5 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-md">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white leading-tight">100% Free</p>
                  <p className="text-xs text-slate-400 mt-0.5">0 commission charged</p>
                </div>
              </div>

              {/* Pillar 2: Direct Call */}
              <div className="flex items-center gap-3.5 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-md">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/15 text-blue-400">
                  <PhoneCall className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white leading-tight">Direct Call</p>
                  <p className="text-xs text-slate-400 mt-0.5">Phone dialer card</p>
                </div>
              </div>

              {/* Pillar 3: Admin Check */}
              <div className="flex items-center gap-3.5 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-md">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-400">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white leading-tight">Admin Check</p>
                  <p className="text-xs text-slate-400 mt-0.5">Verified real devices</p>
                </div>
              </div>

              {/* Pillar 4: Phones & PCs */}
              <div className="flex items-center gap-3.5 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-md">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-purple-500/15 text-purple-400">
                  <Smartphone className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white leading-tight">Phones &amp; PCs</p>
                  <p className="text-xs text-slate-400 mt-0.5">Dedicated niche tag</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
           MAIN WORKSPACE (SIDEBAR + FORM / VIEWS)
           ========================================================================= */}
      <div className="mx-auto w-full max-w-[1280px] px-4 sm:px-6 py-10 flex-1">
        {/* VIEW 1: POST FREE LISTING */}
        {activeTab === "post" && (
          <div>
            {!session ? (
              /* Signed out state */
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center rounded-3xl border border-slate-200 bg-white p-8 sm:p-12 shadow-sm dark:border-white/10 dark:bg-slate-900">
                <div className="space-y-4">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#087DB5]/10 px-3 py-1 text-xs font-bold text-[#087DB5]">
                    <Sparkles className="h-3.5 w-3.5" />
                    Ethiopian Tech Marketplace
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
                    Sign in to post your phone or PC for free
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Thousands of buyers in Addis Ababa and across Ethiopia browse daily for smartphones, laptops, and tablets. Post your device in 2 minutes.
                  </p>
                  <div className="flex gap-3 pt-2">
                    <Link
                      href="/login?callbackUrl=/sell"
                      className="inline-flex items-center gap-2 rounded-xl bg-[#087DB5] px-6 py-3 text-sm font-bold text-white shadow-sm hover:bg-[#073B70]"
                    >
                      <LogIn className="h-4 w-4" />
                      Sign In to Post
                    </Link>
                    <Link
                      href="/signup"
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:text-slate-200"
                    >
                      Create Account
                    </Link>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center dark:border-white/10 dark:bg-slate-800/50">
                  <Smartphone className="mx-auto h-12 w-12 text-[#087DB5]" />
                  <h3 className="mt-3 text-lg font-bold">100% Free Individual Selling</h3>
                  <p className="mt-1 text-xs text-slate-500">
                    Post your phone number so buyers call you directly without intermediary fees.
                  </p>
                </div>
              </div>
            ) : (
              /* Signed in: EXACT TWO-COLUMN LAYOUT FROM REFERENCE HTML */
              <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-8 items-start">
                {/* 1. SIDEBAR (GUIDE + PREVIEW + VENDOR UPSELL) */}
                <aside className="space-y-6">
                  {/* Stepper Guide Card */}
                  <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-[#087DB5] dark:bg-blue-950/40">
                        <BookOpen className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-900 dark:text-white">How Selling Works</h3>
                        <p className="text-[11px] text-slate-500">Simple 4-step workflow</p>
                      </div>
                    </div>

                    {/* Stepper Timeline */}
                    <div className="relative pl-6 space-y-5 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-[2px] before:bg-slate-200 dark:before:bg-slate-700">
                      {/* Step 1 */}
                      <div className="relative">
                        <span className="absolute -left-6 top-0 flex h-5 w-5 items-center justify-center rounded-full bg-[#087DB5] text-white text-[10px] font-bold outline-4 outline-white dark:outline-slate-900">
                          1
                        </span>
                        <p className="text-xs font-bold text-slate-900 dark:text-white leading-tight">
                          Select Device &amp; Details
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                          Pick condition, storage tier, battery percentage and accurate model info.
                        </p>
                      </div>

                      {/* Step 2 */}
                      <div className="relative">
                        <span className="absolute -left-6 top-0 flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold outline-4 outline-white dark:bg-slate-800 dark:text-slate-300 dark:outline-slate-900">
                          2
                        </span>
                        <p className="text-xs font-bold text-slate-900 dark:text-white leading-tight">
                          Add Device Photos
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                          Upload clear pictures of screens, physical corners, serial and cables.
                        </p>
                      </div>

                      {/* Step 3 */}
                      <div className="relative">
                        <span className="absolute -left-6 top-0 flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold outline-4 outline-white dark:bg-slate-800 dark:text-slate-300 dark:outline-slate-900">
                          3
                        </span>
                        <p className="text-xs font-bold text-slate-900 dark:text-white leading-tight">
                          Provide Contact Phone
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                          Your active Ethio Telecom or Safaricom number for instant dial-ins.
                        </p>
                      </div>

                      {/* Step 4 */}
                      <div className="relative">
                        <span className="absolute -left-6 top-0 flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold outline-4 outline-white dark:bg-slate-800 dark:text-slate-300 dark:outline-slate-900">
                          4
                        </span>
                        <p className="text-xs font-bold text-slate-900 dark:text-white leading-tight">
                          Admin Approval &amp; Go Live
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                          Our team reviews and validates your post in under 60 minutes.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Live Calling Card Preview (Reactive) */}
                  <div className="rounded-3xl border border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950 p-6 text-white shadow-md">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-sky-400">
                        Public Listing Preview
                      </span>
                      <span className="rounded bg-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold text-emerald-400">
                        Buyer View
                      </span>
                    </div>

                    <h4 className="text-xs font-bold text-white">Direct Buyer Calling Card</h4>
                    <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                      Buyers on your post tap to dial your phone immediately without chatting intermediaries:
                    </p>

                    <div className="mt-3.5 rounded-2xl border border-white/10 bg-white/5 p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-500/30 text-blue-300 font-bold text-xs">
                          ET
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-400">Direct Seller Contact</p>
                          <p className="font-mono text-sm font-bold text-white">
                            {livePhone.trim() ? livePhone : "+251 91 123 4567"}
                          </p>
                        </div>
                      </div>

                      <div className="w-full rounded-xl bg-emerald-700 py-2 text-center text-xs font-semibold text-white flex items-center justify-center gap-1.5 shadow-sm">
                        <PhoneCall className="h-3.5 w-3.5" />
                        Call Seller Directly
                      </div>
                    </div>
                  </div>

                  {/* Commercial Upsell */}
                  <div className="flex items-start gap-3 rounded-3xl border border-blue-100 bg-gradient-to-br from-blue-50/70 to-slate-50 p-5 dark:border-white/10 dark:bg-slate-900">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#087DB5] text-white shadow-2xs">
                      <Store className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-900 dark:text-white">Commercial Retailer or Shop?</h4>
                      <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-0.5 leading-snug">
                        If you want to sell products beyond phones and PCs, open an official storefront and manage bulk inventory.
                      </p>
                      <button
                        type="button"
                        onClick={() => handleTabChange("vendor")}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#087DB5] hover:underline"
                      >
                        Apply as Commercial Vendor
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </aside>

                {/* 2. MAIN FORM PANEL */}
                <section id="listing-form">
                  <CustomerListingForm
                    onPhoneChange={(phone) => setLivePhone(phone)}
                    onSuccess={() => {
                      toast.success("Listing submitted! View its status below.");
                      handleTabChange("my-listings");
                    }}
                  />
                </section>
              </div>
            )}
          </div>
        )}

        {/* VIEW 2: MY LISTINGS */}
        {activeTab === "my-listings" && (
          <div className="w-full">
            {!session ? (
              <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm dark:border-white/10 dark:bg-slate-900">
                <Tag className="mx-auto h-12 w-12 text-[#087DB5]" />
                <h2 className="mt-4 text-xl font-bold">Sign in to view your listings</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Track approved listings, inspect rejection reasons, and edit or resubmit.
                </p>
                <Link
                  href="/login?callbackUrl=/sell?tab=my-listings"
                  className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#087DB5] px-6 py-2.5 text-sm font-bold text-white shadow-sm"
                >
                  <LogIn className="h-4 w-4" />
                  Sign In
                </Link>
              </div>
            ) : (
              <MyListingsView />
            )}
          </div>
        )}

        {/* VIEW 3: COMMERCIAL VENDOR APPLICATION */}
        {activeTab === "vendor" && (
          <div className="w-full">
            {!session ? (
              <div className="mx-auto max-w-xl rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-white/10 dark:bg-slate-900">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#087DB5]/10 text-[#087DB5] mb-4">
                  <Store className="h-7 w-7" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                  Commercial Vendor Portal
                </h2>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                  Register your shop, retail store, or wholesale business on MOVA Market to manage multi-category inventory, access seller analytics, and receive payouts.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                  <Link
                    href="/signup?type=vendor&callbackUrl=/vendor"
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#087DB5] px-6 py-3 text-sm font-bold text-white shadow-sm hover:bg-[#073B70]"
                  >
                    <Store className="h-4 w-4" />
                    Register as Commercial Vendor
                  </Link>
                  <Link
                    href="/login?callbackUrl=/sell?tab=vendor"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <LogIn className="h-4 w-4" />
                    Sign In
                  </Link>
                </div>
              </div>
            ) : existingVendor ? (
              <div className="mx-auto max-w-2xl rounded-3xl border border-slate-200 bg-white p-8 shadow-sm dark:border-white/10 dark:bg-slate-900">
                <div className="flex items-center gap-3">
                  <Store className="h-8 w-8 text-[#087DB5]" />
                  <div>
                    <h2 className="text-xl font-bold">Commercial Vendor Application</h2>
                    <p className="text-xs text-slate-500">Official Retailer Store</p>
                  </div>
                </div>

                <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-slate-800">
                  <p className="text-sm">
                    Your vendor application status:{" "}
                    <strong className="text-[#073B70] dark:text-[#00B7B0] text-base">{existingVendor.status}</strong>
                  </p>
                  {existingVendor.status === "ACTIVE" && (
                    <div className="mt-4">
                      <Link
                        href="/vendor"
                        className="inline-flex items-center gap-2 rounded-xl bg-[#087DB5] px-5 py-2 text-sm font-bold text-white shadow-sm"
                      >
                        Open Vendor Dashboard
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8 items-start">
                {/* Vendor Benefits */}
                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900 space-y-4">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-7 w-7 text-[#087DB5]" />
                    <div>
                      <h3 className="font-bold text-sm">Commercial Storefront</h3>
                      <p className="text-xs text-slate-500">For retail shops &amp; distributors</p>
                    </div>
                  </div>

                  <div className="space-y-3 text-xs text-slate-600 dark:text-slate-300 pt-2">
                    <div className="flex items-start gap-2.5">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-slate-900 dark:text-white">Multi-Category Inventory</p>
                        <p className="text-[11px] text-slate-500">Sell across all MOVA categories.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2.5">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-slate-900 dark:text-white">Dedicated Store URL</p>
                        <p className="text-[11px] text-slate-500">Your custom branded profile.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2.5">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-slate-900 dark:text-white">Payouts &amp; Analytics</p>
                        <p className="text-[11px] text-slate-500">Full financial reporting &amp; withdrawals.</p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => handleTabChange("post")}
                      className="text-xs font-bold text-[#087DB5] hover:underline"
                    >
                      ← Back to Free Phone / PC Listing
                    </button>
                  </div>
                </div>

                {/* Vendor Application Form */}
                <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-9 shadow-sm dark:border-white/10 dark:bg-slate-900">
                  <div className="border-b border-slate-100 pb-4 dark:border-white/10">
                    <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">
                      Apply as a Commercial Vendor
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Administrators review all applications before enabling the merchant console.
                    </p>
                  </div>

                  <div className="mt-6 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                        Business Name *
                        <input
                          value={vendorForm.businessName}
                          onChange={(e) =>
                            setVendorForm((f) => ({ ...f, businessName: e.target.value }))
                          }
                          placeholder="Official company name"
                          className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:bg-white focus:border-[#087DB5] dark:border-white/10 dark:bg-slate-800 dark:text-white"
                        />
                      </label>

                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                        Owner Name
                        <input
                          value={vendorForm.ownerName}
                          onChange={(e) =>
                            setVendorForm((f) => ({ ...f, ownerName: e.target.value }))
                          }
                          placeholder="Full name of owner"
                          className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:bg-white focus:border-[#087DB5] dark:border-white/10 dark:bg-slate-800 dark:text-white"
                        />
                      </label>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                        Contact Phone
                        <input
                          value={vendorForm.phone}
                          onChange={(e) =>
                            setVendorForm((f) => ({ ...f, phone: e.target.value }))
                          }
                          placeholder="e.g. +251 91 123 4567"
                          className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:bg-white focus:border-[#087DB5] dark:border-white/10 dark:bg-slate-800 dark:text-white"
                        />
                      </label>

                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                        Vendor Industry
                        <select
                          value={vendorForm.vendorTypeId}
                          onChange={(e) =>
                            setVendorForm((f) => ({ ...f, vendorTypeId: e.target.value }))
                          }
                          className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:bg-white focus:border-[#087DB5] dark:border-white/10 dark:bg-slate-800 dark:text-white"
                        >
                          <option value="">Select a type…</option>
                          {types.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                        Business License #
                        <input
                          value={vendorForm.businessLicense}
                          onChange={(e) =>
                            setVendorForm((f) => ({ ...f, businessLicense: e.target.value }))
                          }
                          placeholder="e.g. BL-12345"
                          className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:bg-white focus:border-[#087DB5] dark:border-white/10 dark:bg-slate-800 dark:text-white"
                        />
                      </label>

                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                        Tax ID / TIN
                        <input
                          value={vendorForm.taxId}
                          onChange={(e) =>
                            setVendorForm((f) => ({ ...f, taxId: e.target.value }))
                          }
                          placeholder="e.g. 0012345678"
                          className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:bg-white focus:border-[#087DB5] dark:border-white/10 dark:bg-slate-800 dark:text-white"
                        />
                      </label>
                    </div>

                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                      Store Address
                      <input
                        value={vendorForm.address}
                        onChange={(e) =>
                          setVendorForm((f) => ({ ...f, address: e.target.value }))
                        }
                        placeholder="e.g. Bole Road, Mega Building 3rd Floor, Addis Ababa"
                        className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:bg-white focus:border-[#087DB5] dark:border-white/10 dark:bg-slate-800 dark:text-white"
                      />
                    </label>

                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                      Business Description
                      <textarea
                        rows={3}
                        value={vendorForm.description}
                        onChange={(e) =>
                          setVendorForm((f) => ({ ...f, description: e.target.value }))
                        }
                        placeholder="Brief summary of your company and operations…"
                        className="mt-1.5 w-full rounded-xl border border-slate-300 bg-slate-50 p-3 text-sm text-slate-900 outline-none focus:bg-white focus:border-[#087DB5] dark:border-white/10 dark:bg-slate-800 dark:text-white"
                      />
                    </label>

                    <div className="pt-2">
                      <Button
                        disabled={submittingVendor || !vendorForm.businessName}
                        onClick={() => void submitVendorApplication()}
                        className="h-11 px-7 font-bold bg-[#087DB5] hover:bg-[#073B70] text-white shadow-sm"
                      >
                        {submittingVendor ? "Submitting…" : "Submit Application"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
